<app-dialog
  #dialog
  [key]="'modal-economy-operations'"
  [action]="'view-economy-operations'"
  [headerTheme]="'simple'"
  [title]="'Operações do cenário de economia'"
  [maxWidth]="'960px'">
  <div class="eco-ops">
    <!-- Faixa de resumo: benchmark + contagem + totais (seguem o filtro) -->
    <div class="eco-ops__summary">
      <div class="eco-ops__summary-meta">
        @if (benchmarkLabel) {
          <span class="eco-ops__bench">vs {{ benchmarkLabel }}</span>
        }
        <span class="eco-ops__count" aria-live="polite">
          @if (isFiltered()) {
            {{ visibleOperations().length }} de {{ operations().length }} operações
          } @else {
            {{ operations().length }} operações elegíveis
          }
        </span>
      </div>
      <div class="eco-ops__totals">
        <div class="eco-ops__total-tile eco-ops__total-tile--indigo">
          <span class="eco-ops__total-label">
            Prevista Inicial
            @if (isFiltered()) {
              <span class="eco-ops__total-scope">no filtro</span>
            }
          </span>
          <span class="eco-ops__total-value" [class.is-negative]="totalInitial() < 0">
            R$&nbsp;{{ totalInitial() | vaultFormatCurrency: 2 }}
          </span>
        </div>
        <div class="eco-ops__total-tile eco-ops__total-tile--green">
          <span class="eco-ops__total-label">
            Prevista até Final
            @if (isFiltered()) {
              <span class="eco-ops__total-scope">no filtro</span>
            }
          </span>
          <span class="eco-ops__total-value" [class.is-negative]="totalFinal() < 0">
            R$&nbsp;{{ totalFinal() | vaultFormatCurrency: 2 }}
          </span>
        </div>
      </div>
    </div>

    <!-- Barra de ferramentas: busca, moeda e banco -->
    <div class="eco-ops__toolbar" role="search">
      <app-search
        class="eco-ops__search"
        placeholder="Buscar por LOAN, banco ou contrato"
        [ngModel]="searchQuery()"
        (ngModelChange)="searchQuery.set($event ?? '')"></app-search>
      @if (currencyOptions().length > 1) {
        <app-select
          class="eco-ops__filter"
          theme="clean"
          placeholder="Todas as moedas"
          selectedItemsLabel="{0} moedas"
          [multiple]="true"
          [hasFilter]="false"
          [maxSelectedLabels]="2"
          [options]="currencyOptions()"
          [ngModel]="selectedCurrencies()"
          (ngModelChange)="selectedCurrencies.set($event ?? [])" />
      }
      @if (bankOptions().length > 1) {
        <app-select
          class="eco-ops__filter eco-ops__filter--bank"
          theme="clean"
          placeholder="Todos os bancos"
          selectedItemsLabel="{0} bancos"
          [multiple]="true"
          [maxSelectedLabels]="1"
          [options]="bankOptions()"
          [ngModel]="selectedBanks()"
          (ngModelChange)="selectedBanks.set($event ?? [])" />
      }
    </div>

    @if (visibleOperations().length) {
      <!-- Tabela de operações: cabeçalho fixo ao rolar -->
      <div class="eco-ops__table-wrapper">
        <table class="eco-ops__table" aria-label="Operações do cenário de economia">
          <thead>
            <tr>
              <th scope="col" [attr.aria-sort]="ariaSort('operation')">
                <button type="button" class="eco-ops__sort" (click)="toggleSort('operation')">
                  Operação
                  <i
                    class="pi eco-ops__sort-icon"
                    aria-hidden="true"
                    [ngClass]="sortIcon('operation')"></i>
                </button>
              </th>
              <th scope="col" class="eco-ops__col--center eco-ops__col--currency">Moeda</th>
              @for (column of sortableColumns; track column.key) {
                <th
                  scope="col"
                  class="eco-ops__col--right eco-ops__col--value"
                  [class.eco-ops__col--sorted]="sortKey() === column.key"
                  [attr.aria-sort]="ariaSort(column.key)">
                  <button
                    type="button"
                    class="eco-ops__sort eco-ops__sort--right"
                    (click)="toggleSort(column.key)">
                    {{ column.label }}
                    <i
                      class="pi eco-ops__sort-icon"
                      aria-hidden="true"
                      [ngClass]="sortIcon(column.key)"></i>
                  </button>
                </th>
              }
              <!-- Celular: os três valores numa coluna só, com a ordenação em botões curtos. -->
              <th scope="col" class="eco-ops__col--right eco-ops__col--mobile">
                <span class="eco-ops__mobile-sorts">
                  @for (column of sortableColumns; track column.key) {
                    <button
                      type="button"
                      class="eco-ops__sort eco-ops__sort--right"
                      [class.eco-ops__col--sorted]="sortKey() === column.key"
                      [attr.aria-label]="sortAriaLabel(column.key, column.label)"
                      (click)="toggleSort(column.key)">
                      {{ column.shortLabel }}
                      <i
                        class="pi eco-ops__sort-icon"
                        aria-hidden="true"
                        [ngClass]="sortIcon(column.key)"></i>
                    </button>
                  }
                </span>
              </th>
              <th scope="col" class="eco-ops__col--actions" aria-label="Abrir detalhes"></th>
            </tr>
          </thead>
          <tbody>
            @for (op of visibleOperations(); track op.loanOperationId) {
              <tr
                class="eco-ops__row"
                role="button"
                tabindex="0"
                [attr.aria-label]="'Abrir detalhes de ' + buildOperationLabel(op)"
                (click)="openLoanDetails(op.loanOperationId)"
                (keydown.enter)="openLoanDetails(op.loanOperationId)"
                (keydown.space)="openLoanDetails(op.loanOperationId)">
                <td class="eco-ops__col--op">
                  <!-- flex num div: td com display flex deixa de ser célula e desalinha a linha -->
                  <div class="eco-ops__op">
                    <span class="eco-ops__op-head">
                      <span class="eco-ops__op-code">{{ op.loanCode }}</span>
                      <span class="eco-ops__currency-pill eco-ops__currency-pill--inline">
                        {{ op.currencyCode }}
                      </span>
                    </span>
                    @if (op.bankName) {
                      <span class="eco-ops__op-sub">
                        <app-bank-logo
                          [code]="op.bankCompensationCode"
                          [maxWidth]="14"
                          [marginRight]="0"></app-bank-logo>
                        <span class="eco-ops__op-sub-text">{{ op.bankName }}</span>
                      </span>
                    }
                    @if (op.reference) {
                      <span
                        class="eco-ops__op-ref"
                        [pTooltip]="op.reference"
                        tooltipPosition="top"
                        [showDelay]="300">
                        {{ op.reference }}
                      </span>
                    }
                  </div>
                </td>
                <td class="eco-ops__col--center eco-ops__col--currency">
                  <span class="eco-ops__currency-pill">{{ op.currencyCode }}</span>
                </td>
                <td
                  class="eco-ops__col--right eco-ops__col--value eco-ops__value eco-ops__value--indigo"
                  [class.is-negative]="op.economiaPrevista < 0">
                  R$&nbsp;{{ op.economiaPrevista | vaultFormatCurrency: 2 }}
                </td>
                <td
                  class="eco-ops__col--right eco-ops__col--value eco-ops__value"
                  [class.is-negative]="op.economiaReal < 0">
                  R$&nbsp;{{ op.economiaReal | vaultFormatCurrency: 2 }}
                </td>
                <td
                  class="eco-ops__col--right eco-ops__col--value eco-ops__value eco-ops__diff"
                  [class.is-up]="difference(op) > 0.005"
                  [class.is-down]="difference(op) < -0.005">
                  @if (difference(op) > 0.005) {
                    +
                  }
                  R$&nbsp;{{ difference(op) | vaultFormatCurrency: 2 }}
                </td>
                <td class="eco-ops__col--right eco-ops__col--mobile">
                  <div class="eco-ops__mobile-values">
                    <span
                      class="eco-ops__value eco-ops__value--indigo"
                      [class.is-negative]="op.economiaPrevista < 0">
                      <small>Inicial</small>
                      R$&nbsp;{{ op.economiaPrevista | vaultFormatCurrency: 2 }}
                    </span>
                    <span class="eco-ops__value" [class.is-negative]="op.economiaReal < 0">
                      <small>Até Final</small>
                      R$&nbsp;{{ op.economiaReal | vaultFormatCurrency: 2 }}
                    </span>
                    <span
                      class="eco-ops__value eco-ops__diff"
                      [class.is-up]="difference(op) > 0.005"
                      [class.is-down]="difference(op) < -0.005">
                      <small>Dif.</small>
                      @if (difference(op) > 0.005) {
                        +
                      }
                      R$&nbsp;{{ difference(op) | vaultFormatCurrency: 2 }}
                    </span>
                  </div>
                </td>
                <td class="eco-ops__col--actions">
                  <i class="pi pi-chevron-right eco-ops__chevron" aria-hidden="true"></i>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="eco-ops__empty">
        <i class="pi pi-search" aria-hidden="true"></i>
        <p class="eco-ops__empty-title">Nenhuma operação encontrada</p>
        <p class="eco-ops__empty-text">Ajuste a busca ou os filtros de moeda e banco.</p>
        <app-button
          label="Limpar filtros"
          icon="filter-slash"
          theme="outline-grey"
          size="small"
          [noMargin]="true"
          (sendButtonClick)="clearFilters()"></app-button>
      </div>
    }

    <div class="eco-ops__footer">
      <p class="eco-ops__hint">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        Clique numa operação para abrir os detalhes do empréstimo. Diferença = Prevista até Final −
        Prevista Inicial.
      </p>
      <div class="eco-ops__footer-actions">
        @if (isFiltered() && visibleOperations().length) {
          <app-button
            label="Limpar filtros"
            icon="filter-slash"
            theme="white"
            size="small"
            [noMargin]="true"
            (sendButtonClick)="clearFilters()"></app-button>
        }
        @if (canSeeHistory) {
          <app-button
            label="Histórico e o que mudou"
            icon="history"
            theme="outline-grey"
            size="small"
            [noMargin]="true"
            (sendButtonClick)="openHistory()"></app-button>
        }
      </div>
    </div>
  </div>
</app-dialog>

<!-- Modal de detalhes do empréstimo, aberta in-app sobre a lista de operações -->
<app-modal-loan-details #loanDetailsModal></app-modal-loan-details>
